<template>
  <Teleport to="body">
    <div :class="['algolia-search-panel', { 'algolia-search-panel--open': isOpen }]" role="dialog" aria-modal="true" aria-label="Search">
      <div class="algolia-search-panel__inner">
        <div class="algolia-search-panel__header">
          <div class="algolia-search-panel__autocomplete-wrapper">
            <div class="algolia-search-panel__autocomplete" ref="autocompleteEl"></div>
            <p v-if="emptySubmitHint" class="algolia-search-panel__hint">Please enter something to search.</p>
          </div>
          <button
            class="algolia-search-panel__close"
            @click="close"
            aria-label="Close search"
          >
            <svg role="img" aria-hidden="true" focusable="false">
              <use href="#close"></use>
            </svg>
          </button>
        </div>
        <div class="algolia-search-panel__content" ref="panelContainerEl">
          <!-- Autocomplete panel renders here -->
        </div>
      </div>
    </div>
    <div
      :class="['algolia-search-panel__backdrop', { 'algolia-search-panel__backdrop--visible': isOpen }]"
      @click="close"
    ></div>
  </Teleport>
</template>

<script setup>
import { ref, nextTick, onBeforeMount, onMounted, onUnmounted } from 'vue';
import { autocomplete } from '@algolia/autocomplete-js';
import { render as preactRender } from 'preact';
import { useSearchData } from './composables/useSearchData';
import {
  getRecentSearchesPlugin,
  getEmptyStateSources,
  getTypingStateSources
} from './autocomplete.config';

/* ─── Debounce ─── */
const DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 2;

function debouncePromise(fn, time) {
  let timer;
  return function debounced(...args) {
    if (timer) clearTimeout(timer);
    return new Promise((resolve) => {
      timer = setTimeout(() => resolve(fn(...args)), time);
    });
  };
}

const debouncedSources = debouncePromise((items) => Promise.resolve(items), DEBOUNCE_MS);

/* ─── Refs ─── */
const isOpen = ref(false);
const emptySubmitHint = ref(false);
const autocompleteEl = ref(null);
const panelContainerEl = ref(null);
let autocompleteInstance = null;
let productsNbHits = 0;
let searchTrackingTimeout = null;
let lastTrackedSearchQuery = '';

const { prefetchEmptyStateData, getTrendingItems, getPopularItems } = useSearchData();

const createAutocompleteInstance = () => {
  if (!autocompleteEl.value || !panelContainerEl.value) {
    return null;
  }

  return autocomplete({
    container: autocompleteEl.value,
    panelContainer: panelContainerEl.value,
    placeholder: 'Search Stio',
    detachedMediaQuery: 'none',
    openOnFocus: true,
    stallThreshold: DEBOUNCE_MS + 300,
    renderer: {
      render(children, root) {
        if (!root || !panelContainerEl.value?.isConnected) {
          return;
        }

        preactRender(children, root);
      },
    },
    onSubmit({ state }) {
      goToSearchResults(state.query);
    },
    onStateChange({ state, prevState }) {
      if (state.query !== prevState.query) {
        emptySubmitHint.value = false;
        scheduleSearchTracking(state.query);
        resetHorizontalProductScroll();
      }

      // Prevent autocomplete from closing its panel while our overlay is open
      if (isOpen.value && prevState.isOpen && !state.isOpen) {
        requestAnimationFrame(() => {
          autocompleteInstance?.setIsOpen(true);
        });
      }
    },
    plugins: [
      getRecentSearchesPlugin(getAutocompleteInstance, close),
    ],
    getSources({ query }) {
      const trimmed = query.trim();
      if (!trimmed || trimmed.length < MIN_QUERY_LENGTH) {
        return getEmptyStateSources(getTrendingItems, getPopularItems, close);
      }
      return debouncedSources(getTypingStateSources(trimmed, close, getNbHits, setNbHits));
    },
  });
};

const destroyAutocompleteInstance = () => {
  autocompleteInstance?.destroy();
  autocompleteInstance = null;
};

/* ─── Open / Close / Toggle ─── */
const open = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'Used_search_box'
  });

  prefetchEmptyStateData();

  isOpen.value = true;
  document.body.style.overflow = 'hidden';
  document.querySelector('.js-search')?.classList.add('is--active');

  requestAnimationFrame(() => {
    const input = autocompleteEl.value?.querySelector('input');
    input?.focus();

    // Force AA panel open + fetch empty-state sources
    autocompleteInstance?.setIsOpen(true);
    autocompleteInstance?.refresh().catch(() => {});
  });
};

const close = () => {
  isOpen.value = false;
  emptySubmitHint.value = false;
  document.body.style.overflow = '';
  document.querySelector('.js-search')?.classList.remove('is--active');
  autocompleteInstance?.setIsOpen(false);
};

const toggle = (_open) => {
  const shouldOpen = typeof _open === 'boolean' ? _open : !isOpen.value;
  shouldOpen ? open() : close();
};

const resetHorizontalProductScroll = () => {
  requestAnimationFrame(() => {
    panelContainerEl.value
      ?.querySelectorAll('[data-autocomplete-source-id="products"] .aa-List, [data-autocomplete-source-id="popular"] .aa-List')
      .forEach((listEl) => {
        listEl.scrollLeft = 0;
      });
  });
};

onBeforeMount(() => {
  window.toggleAlgoliaSearch = toggle;
});

onMounted(() => {
  nextTick(() => {
    destroyAutocompleteInstance();
    autocompleteInstance = createAutocompleteInstance();
  });
});

onUnmounted(() => {
  clearTimeout(searchTrackingTimeout);
  destroyAutocompleteInstance();
});

const getAutocompleteInstance = () => autocompleteInstance;
const getNbHits = () => productsNbHits;
const setNbHits = (count) => { productsNbHits = count; };
const pushSearchTrackingEvent = (query) => {
  const normalizedQuery = query && query.trim();

  if (!normalizedQuery || normalizedQuery === lastTrackedSearchQuery) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'search_term',
    search_term: normalizedQuery,
  });

  lastTrackedSearchQuery = normalizedQuery;
};
const scheduleSearchTracking = (query) => {
  const normalizedQuery = query && query.trim();

  if (!normalizedQuery || normalizedQuery === lastTrackedSearchQuery) {
    return;
  }

  clearTimeout(searchTrackingTimeout);
  searchTrackingTimeout = window.setTimeout(() => {
    pushSearchTrackingEvent(normalizedQuery);
  }, 400);
};
const goToSearchResults = (query) => {
  const normalizedQuery = query && query.trim();

  if (!normalizedQuery) {
    emptySubmitHint.value = true;
    return;
  }

  clearTimeout(searchTrackingTimeout);
  pushSearchTrackingEvent(normalizedQuery);

  close();
  window.location.href = `/search?q=${encodeURIComponent(normalizedQuery)}`;
};
</script>
